import { IconChevronDown, IconChevronUp, IconInbox } from './icons';
import { getIncidentCreatedAtMs } from './incident';
import { IncidentCategoryChip } from './IncidentCategoryChip';
import { IncidentPriorityIndicator } from './IncidentPriorityIndicator';
import { IncidentStatusBadge } from './IncidentStatusBadge';
import { formatRelativeDate, formatAbsoluteDateTime } from '../../lib/formatters';
import type { ExtranetIncidentOutput, IncidentSortDirection } from '../../types/extranet-api.types';
import { TableErrorRow } from '../common/TableErrorRow';

interface IncidentsTableProps {
  items: ExtranetIncidentOutput[];
  total: number;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  error: unknown;
  selectedId: string | null;
  onRowClick: (item: ExtranetIncidentOutput) => void;
  /** Tri serveur, sur la date de création — la seule dimension triable côté back. */
  sortDirection: IncidentSortDirection;
  onSortDirectionChange: (next: IncidentSortDirection) => void;
}

const SKELETON_ROWS = 10;
const COL_COUNT = 9;

/**
 * Les lignes viennent triées et filtrées par le serveur : aucun tri client, qui
 * ne porterait que sur la page courante et mentirait sur l'ordre global.
 */
export const IncidentsTable = ({
  items,
  total,
  isLoading,
  isFetching,
  isError,
  error,
  selectedId,
  onRowClick,
  sortDirection,
  onSortDirectionChange,
}: IncidentsTableProps) => (
  <div className="bg-white border border-zinc-200 rounded-md overflow-hidden">
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b border-zinc-200 bg-zinc-50/40">
            <Th>N° Ticket</Th>
            <SortableTh
              direction={sortDirection}
              onClick={() => onSortDirectionChange(sortDirection === 'asc' ? 'desc' : 'asc')}
            >
              Date
            </SortableTh>
            <Th>N° Course</Th>
            <Th>Catégorie</Th>
            <Th>Ville</Th>
            <Th>Entité</Th>
            <Th>Contrat</Th>
            <Th>Statut</Th>
            <Th align="right">Priorité</Th>
          </tr>
        </thead>
        <tbody className={isFetching && !isLoading ? 'opacity-60 transition-opacity' : ''}>
          {isLoading ? (
            Array.from({ length: SKELETON_ROWS }).map((_, i) => <SkeletonRow key={i} />)
          ) : isError ? (
            <TableErrorRow colSpan={COL_COUNT} error={error} fallbackTitle="Impossible de charger les incidents" />
          ) : items.length === 0 ? (
            <EmptyRow />
          ) : (
            items.map(item => (
              <Row key={item.id} item={item} selected={item.id === selectedId} onClick={() => onRowClick(item)} />
            ))
          )}
        </tbody>
      </table>
    </div>
    {!isLoading && items.length > 0 ? (
      <div className="px-4 py-2.5 text-xs text-zinc-500 border-t border-zinc-100 flex items-center justify-between">
        <span>
          <span className="font-mono tabular-nums text-zinc-700">{items.length}</span> affiché
          {items.length > 1 ? 's' : ''} sur <span className="font-mono tabular-nums text-zinc-700">{total}</span>
        </span>
        <span className="text-zinc-400">Cliquez sur une ligne pour le détail</span>
      </div>
    ) : null}
  </div>
);

const Th = ({ children, align = 'left' }: { children: React.ReactNode; align?: 'left' | 'right' }) => (
  <th
    className={`px-4 py-2.5 text-xs font-medium uppercase tracking-wide text-zinc-500 ${align === 'right' ? 'text-right' : 'text-left'}`}
  >
    {children}
  </th>
);

const SortableTh = ({
  children,
  direction,
  onClick,
}: {
  children: React.ReactNode;
  direction: IncidentSortDirection;
  onClick: () => void;
}) => (
  <th className="px-4 py-2.5 text-left">
    <button
      type="button"
      onClick={onClick}
      aria-label={`Trier par date, ordre ${direction === 'asc' ? 'décroissant' : 'croissant'}`}
      className="inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-zinc-900 transition-colors"
    >
      <span>{children}</span>
      {direction === 'asc' ? (
        <IconChevronUp width={11} height={11} className="text-zinc-700" />
      ) : (
        <IconChevronDown width={11} height={11} className="text-zinc-700" />
      )}
    </button>
  </th>
);

const Row = ({ item, selected, onClick }: { item: ExtranetIncidentOutput; selected: boolean; onClick: () => void }) => {
  const createdAtMs = getIncidentCreatedAtMs(item);
  return (
    <tr
      onClick={onClick}
      className={[
        'border-b border-zinc-100 last:border-b-0 cursor-pointer transition-colors duration-100',
        selected ? 'bg-zinc-50' : 'hover:bg-zinc-50/60',
      ].join(' ')}
    >
      <td className="px-4 py-3 font-mono text-sm tabular-nums text-zinc-900 whitespace-nowrap">{item.id}</td>
      <td
        className="px-4 py-3 font-mono text-sm tabular-nums text-zinc-500 whitespace-nowrap"
        title={formatAbsoluteDateTime(createdAtMs)}
      >
        {formatRelativeDate(createdAtMs)}
      </td>
      <td className="px-4 py-3 font-mono text-sm tabular-nums text-zinc-500 whitespace-nowrap">{item.rideId}</td>
      <td className="px-4 py-3">
        <IncidentCategoryChip category={item.category} />
      </td>
      <td className="px-4 py-3 text-zinc-700">{item.city}</td>
      <td className="px-4 py-3 text-zinc-700">
        <span className="block max-w-[160px] truncate" title={item.entityName}>
          {item.entityName}
        </span>
      </td>
      <td className="px-4 py-3 font-mono text-sm tabular-nums text-zinc-500 whitespace-nowrap">{item.contractRef}</td>
      <td className="px-4 py-3 whitespace-nowrap">
        <IncidentStatusBadge status={item.status} />
      </td>
      <td className="px-4 py-3 text-right whitespace-nowrap">
        <IncidentPriorityIndicator priority={item.priority} />
      </td>
    </tr>
  );
};

const SkeletonRow = () => (
  <tr className="border-b border-zinc-100 last:border-b-0">
    {Array.from({ length: COL_COUNT }).map((_, i) => (
      <td key={i} className="px-4 py-3">
        <div
          className="h-3 rounded-sm skeleton-pulse"
          style={{ width: `${[60, 50, 80, 70, 40, 55, 65, 45, 35][i]}%` }}
        />
      </td>
    ))}
  </tr>
);

const EmptyRow = () => (
  <tr>
    <td colSpan={COL_COUNT} className="px-4 py-16">
      <div className="flex flex-col items-center justify-center gap-3 text-center">
        <IconInbox width={28} height={28} className="text-zinc-300" />
        <div>
          <p className="text-sm text-zinc-900">Aucun incident sur cette période.</p>
          <p className="text-xs text-zinc-500 mt-1">
            Ajustez les filtres ou élargissez la période pour afficher davantage de résultats.
          </p>
        </div>
      </div>
    </td>
  </tr>
);
